import { type Path, useNavigate } from '@exam-center/router';
import { Flex, Text } from '@shared/components';
import { isURL } from '@shared/utils';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';

import { notificationIconMapper } from '../../containers/notification-drawer/notification-icon-mapper';

export interface NotificationItemProps {
  id: string;
  createdAt: Date;
  notification: {
    title: string;
    message: string | null;
    type: string;
    ref: keyof typeof notificationIconMapper;
    actionUrl: string | null;
  } | null;
}

export default function NotificationItem(
  props: Readonly<{ notificationRecipient: NotificationItemProps; close: () => void }>,
) {
  const { notificationRecipient, close } = props;
  const navigate = useNavigate();

  if (!notificationRecipient || !notificationRecipient.notification) {
    return null;
  }

  const actionUrl = notificationRecipient.notification.actionUrl;
  const hasValidActionUrl = Boolean(actionUrl && isURL(actionUrl));

  return (
    <Flex
      key={notificationRecipient.id}
      sx={(theme) => ({
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: theme.spacing(4),
        p: theme.spacing(4),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: hasValidActionUrl ? 'pointer' : 'default',
        ...(hasValidActionUrl && {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      })}
      onClick={() => {
        if (hasValidActionUrl && actionUrl) {
          try {
            const parsedUrl = new URL(actionUrl);
            if (parsedUrl.origin === window.location.origin) {
              const fullPath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
              navigate(fullPath as Path);
            } else {
              // Open external URLs in a new tab
              window.open(actionUrl, '_blank', 'noopener,noreferrer');
            }
          } catch {
            // Fallback - full page navigation
            window.location.href = actionUrl;
          }
          close();
        }
      }}
    >
      <Flex
        sx={(theme) => ({
          width: 32,
          height: 32,
          borderRadius: 1,
          backgroundColor: theme.palette.primary.lighter,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        })}
      >
        {notificationIconMapper[notificationRecipient.notification.ref]}
      </Flex>

      <Flex
        sx={(theme) => ({
          flex: 1,
          flexDirection: 'column',
          gap: theme.spacing(2),
        })}
      >
        <Flex
          sx={{
            flexDirection: 'row',
            alignItems: 'start',
            justifyContent: 'space-between',
          }}
        >
          <Text variant='body1Semibold'>{notificationRecipient.notification.title}</Text>

          <Text
            variant='body2'
            color='secondary.main'
            sx={{ whiteSpace: 'nowrap', marginTop: 0.5 }}
          >
            {dayjs(notificationRecipient.createdAt).format('DD-MM-YYYY, HH:mm')}
          </Text>
        </Flex>

        <Text
          color='text.secondary'
          variant='body2'
          component='div'
          dangerouslySetInnerHTML={{
            __html: (() => {
              const html = notificationRecipient.notification.message ?? '';
              try {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                doc.querySelectorAll('script,style').forEach((n) => n.remove());
                const sanitizedHTML = DOMPurify.sanitize(doc.body.innerHTML);
                return sanitizedHTML;
              } catch {
                return '';
              }
            })(),
          }}
        />
      </Flex>
    </Flex>
  );
}
